/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  RegisterOptions,
  useController,
  useFormContext,
} from "react-hook-form";
import { useEffect, useRef, useState, useMemo } from "react";
import classNames from "classnames";
import { IMaskInput as RealIMaskInput } from "react-imask";
import useClickOutside from "hooks/useClickOutside";
import Locked from "assets/icons/glory/locked.svg?react";
import Tooltip from "../Tooltip";
import "./styles.sass";

export type InputMaskProps = {
  disabled?: boolean;
  copied?: boolean;
  name: string;
  label?: string;
  placeholder?: string;
  error?: string | null;
  mode?: "light" | "dark";
  suffix?: string;
  help?: string;
  validation?: RegisterOptions;
  tooltipUp?: React.ReactNode;
  tooltipDown?: React.ReactNode;
  type?: "text" | "number" | "password" | "email" | "tel";
  inputMode?: "numeric" | "decimal";
  value: string;
  mask: string;
  unmask?: boolean;
  upperCase?: boolean;
  lazy?: boolean;
  onBlur?: (value: string, props: InputMaskProps) => void;
  onChange?: (value: string, props: InputMaskProps) => void;
  onFocus?: (value: string, props: InputMaskProps) => void;
};

const InputMask = (props: InputMaskProps) => {
  const { control } = useFormContext();
  const {
    disabled = false,
    copied = false,
    error,
    name,
    label,
    mode = import.meta.env.VITE_APP_PROJECT_NAME === "banger"
      ? "dark"
      : "light",
    help,
    validation,
    placeholder,
    type = "text",
    inputMode,
    tooltipUp,
    tooltipDown,
    value = "",
    suffix,
    mask,
    unmask,
    upperCase = false,
    lazy = true,
    onChange,
  } = props;

  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const tooltipTriggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const inputRef =
    useRef<Nullable<{ element: HTMLInputElement | null; maskRef: any }>>(null);
  const labelRef = useRef(null);
  const supportTextRef = useRef<HTMLDivElement>(null);

  const componentRef = useRef(null);
  const { field: fieldController } = useController({
    control,
    name,
    rules: validation,
    defaultValue: value,
  });

  const handleReset = (e: React.MouseEvent) => {
    e.preventDefault();
    if (inputRef.current?.maskRef) {
      inputRef.current.maskRef.value = "";
      inputRef.current.maskRef.updateValue(); // обновляем принудительно
    }
    fieldController.onChange("");
    onChange?.("", props);
    inputRef.current?.element?.focus();
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    if (inputRef.current?.element?.value) {
      navigator.clipboard.writeText(inputRef.current.element.value);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // eslint-disable-next-line react/destructuring-assignment
    props.onFocus?.(fieldController.value, props);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // eslint-disable-next-line react/destructuring-assignment
    props.onBlur?.(fieldController.value, props);
  };

  const tooltipTrigger = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsTooltipVisible((prev) => !prev);
  };

  const tooltipClickOutsideHandler = () => {
    setIsTooltipVisible(false);
  };

  const applyCase = (val: string) => (upperCase ? val.toUpperCase() : val);

  const handleAccept = (val: any) => {
    const formatted = applyCase(val);
    fieldController.onChange(formatted);
    onChange?.(formatted, props);
  };

  const showSupportText = () => {
    if (supportTextRef.current) {
      supportTextRef.current.style.display = "block";
    }
  };

  const hideSupportText = () => {
    if (supportTextRef.current) {
      supportTextRef.current.style.display = "none";
    }
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const inputElement = inputRef.current?.element;

    if (inputElement && supportTextRef.current) {
      inputElement?.addEventListener("focus", showSupportText);
      inputElement?.addEventListener("blur", hideSupportText);
      return () => {
        inputElement?.removeEventListener("focus", showSupportText);
        inputElement?.removeEventListener("blur", hideSupportText);
      };
    }
  }, []);

  useClickOutside({
    refs: [tooltipRef, tooltipTriggerRef],
    handler: tooltipClickOutsideHandler,
  });

  const componentClass = classNames("InputMask", {
    "InputMask__with-error": !!error,
    [mode]: !!mode,
  });

  const nameClass = classNames("InputMask__name", {
    withValue: !!fieldController.value,
    error: !!error,
    disabled: !!disabled,
    copied: !!copied,
    tooltip: !!tooltipUp || !!tooltipDown,
  });

  const inputClass = classNames("InputMask__input", {
    withValue: !!fieldController.value,
    dark: mode === "dark",
  });

  const borderClass = classNames("InputMask__border", {
    dark: mode === "dark",
    withValue: !!fieldController.value,
    error: !!error,
    disabled: !!disabled,
  });

  const supportClass = classNames("InputMask__support", {
    dark: mode === "dark",
    error: !!error,
  });

  const clearClass = classNames("InputMask__clear", {
    tooltip: !!tooltipUp || !!tooltipDown,
    dark: mode === "dark",
  });

  const suffixClass = classNames("InputMask__suffix", {
    error: !!error,
  });

  const attentionClass = classNames("InputMask__attention", {
    tooltip: !!tooltipUp || !!tooltipDown,
    suffix: !!suffix,
  });

  // eslint-disable-next-line consistent-return
  const supportText = useMemo(() => {
    if (help && !error) {
      return help;
    }
    if (error) {
      return error;
    }
  }, [help, error]);

  return (
    <div className={componentClass} ref={componentRef}>
      <label className={nameClass} ref={labelRef}>
        <RealIMaskInput
          className={inputClass}
          placeholder={placeholder}
          {...fieldController}
          ref={inputRef}
          type={type}
          inputMode={inputMode}
          disabled={disabled}
          mask={mask}
          unmask={unmask}
          lazy={lazy}
          value={fieldController.value}
          onAccept={handleAccept}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {suffix && !!fieldController.value.length && !disabled && (
          <div className={suffixClass}>{suffix}</div>
        )}
        {fieldController.value.length && !disabled ? (
          // eslint-disable-next-line react/button-has-type, jsx-a11y/control-has-associated-label
          <button className={clearClass} onClick={handleReset} />
        ) : null}
        {!!error && !disabled && <div className={attentionClass} />}
        {disabled && !copied && (
          <div className="Input__disabled">
            <Locked />
          </div>
        )}
        {disabled && copied && (
          <button
            type="button"
            className="InputMask__copied"
            onClick={handleCopy}
          />
        )}
        {tooltipUp && (
          <>
            <button
              className="InputMask__tooltip"
              // eslint-disable-next-line react/jsx-handler-names
              onClick={tooltipTrigger}
              type="button"
              ref={tooltipTriggerRef}
            />
            <Tooltip
              triggerRef={tooltipTriggerRef}
              isVisible={isTooltipVisible}
              ref={tooltipRef}
              position="up"
            >
              {tooltipUp}
            </Tooltip>
          </>
        )}
        {tooltipDown && (
          <>
            <button
              className="InputMask__tooltip"
              // eslint-disable-next-line react/jsx-handler-names
              onClick={tooltipTrigger}
              type="button"
              ref={tooltipTriggerRef}
            />
            <Tooltip
              triggerRef={tooltipTriggerRef}
              isVisible={isTooltipVisible}
              ref={tooltipRef}
              position="down"
            >
              {tooltipDown}
            </Tooltip>
          </>
        )}
        <div className={borderClass}>
          <div className="InputMask__border_left" />
          <div className="InputMask__border_center">
            <span className="InputMask__label">{label}</span>
          </div>
          <div className="InputMask__border_right" />
        </div>
      </label>
      {supportText && !disabled && (
        <div
          className={`${supportClass} ${isFocused ? "show" : ""}`}
          ref={supportTextRef}
        >
          {supportText}
        </div>
      )}
    </div>
  );
};

export default InputMask;
